import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "./services/firebase";

function settingsKey(slug) {
  return ["practice-settings", slug];
}

async function fetchSettings(slug) {
  const snap = await getDoc(doc(db, "practices", slug));
  return snap.exists() ? snap.data().settings || {} : {};
}

/**
 * Reads and updates a practice's admin settings, stored at
 * practices/{slug}.settings. Writes are optimistic and merge-only, so toggling
 * one control never clobbers the others (or the practice's name/slug).
 *
 * @param {string} slug - Practice slug (the Firestore practices doc id).
 * @param {string} [practiceName] - Kept in sync on the doc so backend email
 *   functions can resolve settings by practice name.
 */
export function usePracticeSettings(slug, practiceName) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: settingsKey(slug),
    queryFn: () => fetchSettings(slug),
    enabled: !!slug,
  });

  const mutation = useMutation({
    mutationFn: async ({ storageKey, key, value }) => {
      await setDoc(
        doc(db, "practices", slug),
        {
          slug,
          ...(practiceName ? { name: practiceName } : {}),
          settings: { [storageKey]: { [key]: value } },
        },
        { merge: true }
      );
    },
    onMutate: async ({ storageKey, key, value }) => {
      await queryClient.cancelQueries({ queryKey: settingsKey(slug) });
      const previous = queryClient.getQueryData(settingsKey(slug));
      queryClient.setQueryData(settingsKey(slug), (old) => {
        const next = { ...(old || {}) };
        next[storageKey] = { ...(next[storageKey] || {}), [key]: value };
        return next;
      });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(settingsKey(slug), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: settingsKey(slug) });
    },
  });

  return {
    settings: query.data,
    isLoading: query.isLoading,
    error: query.error,
    updateSetting: mutation.mutate,
    isSaving: mutation.isPending,
    saveError: mutation.error,
  };
}
